import { Injectable } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	Router,
	RouterStateSnapshot,
	UrlTree
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { StorageKey } from 'src/app/_models/local-storage-key';
import { AuthenticationV2Service } from 'src/app/authentication-v2/services/authentication-v2.service';
import { PaymentLinkStatusEnum } from 'src/app/shared-components/models/payment-status.enum';
import { SharedService } from '../services/shared.service';
import {
	isSeatSession,
	UserAccountStateEnum,
	UserJWTDetailsInterface
} from 'src/app/authentication-v2/models/auth.model';
import { OnboardingFunnelKey } from 'src/app/onboarding/onboarding-funnel.catalogue';
import { OnboardingEntryResolverService } from '../../authentication-v2/modules/onboarding/services/onboarding-entry-resolver.service';

@Injectable({
	providedIn: 'root'
})
export class SmiV2Guard {
	constructor(
		private authV2Service: AuthenticationV2Service,
		private router: Router,
		private sharedService: SharedService,
		private onboardingEntryResolver: OnboardingEntryResolverService
	) {}

	canActivate(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot
	):
		| Observable<boolean | UrlTree>
		| Promise<boolean | UrlTree>
		| boolean
		| UrlTree {
		if (/\/reports\/preview\/[^/]+\/export/.test(state.url)) {
			return true;
		}

		const isUserLoggedIn = this.authV2Service.isUserSignedIn();
		if (!isUserLoggedIn) {
			return this.router.createUrlTree(['/authentication']);
		}

		// A SEAT OWES NEITHER OF THE TWO THINGS BELOW, so it is admitted before
		// either is asked. Both gates are about establishing a brand, and the
		// brand this person joined is already established:
		//
		//   * brand onboarding collects the brand's identity, its competitors
		//     and its integrations. The account has all three;
		//   * the payment branch reads `user_account_state`, which is the seat's
		//     `payment_status` — and `create_member_user` writes PENDING on it,
		//     the exact value that sends a brand off to buy a plan. The seat is
		//     paid for by the owner, so that errand does not exist for them, and
		//     it ends on `/authentication/payment`, a route this app does not
		//     even declare.
		//
		// See `isSeatSession` for why the account-root claim is the signal and
		// `sub_user` is not.
		if (isSeatSession(this.authV2Service.getCurrentDecodedToken())) {
			return true;
		}

		// One read of the recorded progress answers both questions the shell
		// asks — which funnel this brand belongs to and whether it still owes
		// onboarding — so no route is named here.
		return this.onboardingEntryResolver.resolveOnboardingEntry().pipe(
			switchMap(decision => {
				// A failed progress read is not an answer that says "may not
				// proceed" — it is no answer at all. Cancelling the navigation
				// used to strand the person on whatever page they happened to
				// be on (their home page, for a first load) with nothing said.
				// Letting them through leaves the question to the screen
				// behind, which reports its own state either way (design.md,
				// Decision 6).
				if (decision.progressUnknown) {
					return of(true);
				}

				// A brand whose origin names a funnel of its own routes on
				// recorded progress alone, never on the legacy payment signals.
				if (decision.funnel !== OnboardingFunnelKey.BrandDefault) {
					return of(
						decision.owesOnboarding
							? this.router.createUrlTree([decision.route])
							: true
					);
				}

				const userDetails: UserJWTDetailsInterface =
					this.authV2Service.getCurrentDecodedToken();
				const userAccountState = parseInt(
					userDetails.user_account_state
				);
				if (
					userAccountState === UserAccountStateEnum.PENDING ||
					userAccountState === UserAccountStateEnum.ONGOING
				) {
					this.getPaymentLink();
					return of(false);
				}

				return of(
					decision.owesOnboarding
						? this.router.createUrlTree([decision.route])
						: true
				);
			}),
			catchError(err => {
				console.error(
					'SmiV2Guard: onboarding entry check failed, letting the navigation through',
					err
				);
				return of(true);
			})
		);
	}

	public getPaymentLink(): void {
		const token = localStorage.getItem(StorageKey.token);
		this.sharedService
			.getUserPaymentStatus()
			.pipe(take(1))
			.subscribe(
				res => {
					if (res.status === PaymentLinkStatusEnum.SUCCESS) {
						const paymentLink = res.data;
						window.open(paymentLink, '_self');
					} else {
						this.router.navigate(['/authentication/payment'], {
							queryParams: { token }
						});
					}
				},
				err => {}
			);
	}
}
